import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Table,
  Tooltip,
  Typography,
  Upload
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { RcFile, UploadFile, UploadProps } from 'antd/es/upload';
import { UploadChangeParam, UploadFileStatus } from 'antd/es/upload/interface';
import { TFunction } from 'i18next';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OvertimeTicketProgress } from 'src/types/hr_ticket/overtimeTicket.type';
import { showNotification } from 'src/utils/notification';
const { Text } = Typography;
const columns = function (
  formState: OvertimeTicketProgress,
  setFormState: Dispatch<SetStateAction<OvertimeTicketProgress>>,
  callbackSave: () => void,
  callbackEdit: (record: OvertimeTicketProgress) => void,
  callbackCancel: (record: OvertimeTicketProgress) => void,
  beforeUpload: (file: RcFile) => string | false,
  handleChangeAttach: (info: UploadChangeParam<UploadFile<any>>) => void,
  editingKey: string,
  isSupervisorConfirmed: number,
  t: TFunction<'translation', undefined, 'translation'>
): ColumnsType<OvertimeTicketProgress> {
  return [
    {
      title: 'Tên công việc',
      dataIndex: '',
      key: 'jobTitle',
      width: '15%',
      render: (record) => {
        return <Text>{record?.manpowerSupplyFormDetail?.jobTitle}</Text>;
      }
    },
    {
      title: 'Chi tiết công việc',
      dataIndex: '',
      key: 'jobDetail',
      width: '15%',
      align: 'left',
      render: (record) => <Text>{record?.manpowerSupplyFormDetail?.jobDetail}</Text>
    },
    {
      title: 'Yêu cầu đặc thù',
      dataIndex: '',
      key: 'jobRequirement',
      width: '15%',
      align: 'left',
      render: (record) => <Text>{record?.manpowerSupplyFormDetail?.jobRequirement}</Text>
    },
    {
      title: <Text>Xác nhận</Text>,
      dataIndex: 'isCompleted',
      key: 'unconfirm',
      width: '10%',
      align: 'center',
      render: (_, record) => {
        if (formState.manpowerSupplyFormDetailId === record.manpowerSupplyFormDetailId) {
          return (
            <Checkbox
              disabled={formState.manpowerSupplyFormDetailId !== record.manpowerSupplyFormDetailId}
              checked={
                formState.manpowerSupplyFormDetailId === record.manpowerSupplyFormDetailId
                  ? formState.isCompleted
                  : record.isCompleted
              }
              onChange={(e) => {
                setFormState((prev) => ({
                  ...prev,
                  isCompleted: e.target.checked,
                  percentageCompletion: 100
                }));
              }}
            />
          );
        } else {
          return record.isCompleted ? (
            <Text type='success' strong>
              Đã xác nhận
            </Text>
          ) : (
            <Text type='danger' strong>
              Chưa xác nhận
            </Text>
          );
        }
      }
    },
    {
      title: (
        <Space size={[4, 0]}>
          <Tooltip title='Bắt buộc'>
            <Text type='danger'>*</Text>
          </Tooltip>
          <Text>Tiến độ (%)</Text>
        </Space>
      ),
      dataIndex: 'percentageCompletion',
      key: 'percentageCompletion',
      align: 'center',
      render: (_, record) => {
        if (formState.manpowerSupplyFormDetailId === record.manpowerSupplyFormDetailId) {
          return (
            <Form.Item
              name='percentageCompletion'
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: '' }]}
            >
              <InputNumber
                min={0}
                max={100}
                value={formState.percentageCompletion}
                onChange={(value) =>
                  setFormState({
                    ...formState,
                    percentageCompletion: value as number
                  })
                }
              />
            </Form.Item>
          );
        }
        return <Text>{record.percentageCompletion}%</Text>;
      }
    },
    {
      title: (
        <Space size={[4, 0]}>
          <Tooltip title='Bắt buộc'>
            <Text type='danger'>*</Text>
          </Tooltip>
          <Text>Ghi chú</Text>
        </Space>
      ),
      dataIndex: 'note',
      key: 'note',
      width: '15%',
      align: 'left',
      render: (_, record) => {
        if (formState.manpowerSupplyFormDetailId === record.manpowerSupplyFormDetailId) {
          return (
            <Form.Item
              name='note'
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: 'Không được để trống!' }]}
            >
              <Input
                value={formState.note}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    note: e.target.value
                  })
                }
              />
            </Form.Item>
          );
        }
        return <Text>{record.note}</Text>;
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      align: 'center',
      width: '10%',
      render: (_, record) => {
        if (formState.manpowerSupplyFormDetailId === record.manpowerSupplyFormDetailId) {
          return (
            <Space size='small'>
              <Button type='link' size='small' onClick={callbackSave}>
                {t('action.save')}
              </Button>
              <Button type='link' size='small' danger onClick={() => callbackCancel(record)}>
                {t('action.cancel')}
              </Button>
            </Space>
          );
        }
        return (
          <Space size='small'>
            <Button type='link' disabled={editingKey !== ''} size='small' onClick={() => callbackEdit(record)}>
              {t('action.edit')}
            </Button>
          </Space>
        );
      }
    }
  ];
};

type Props = {
  jobConfirms: OvertimeTicketProgress[];
  handleSetFormState: (overtimeTicketProgress: OvertimeTicketProgress[]) => void;
  isSupervisorConfirmed: number;
};

const initialFormState: OvertimeTicketProgress = {
  manpowerSupplyFormDetailId: undefined,
  percentageCompletion: 0,
  isCompleted: false,
  note: undefined,
  overtimeTicketProgressAttach: []
};

export default function AddConfirmJobResult({ jobConfirms, handleSetFormState, isSupervisorConfirmed }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [addMode, setAddMode] = useState<'add' | 'edit'>('add');
  const [editingKey, setEditingKey] = useState('');
  const [formState, setFormState] = useState(initialFormState);
  const [overtimeTicketProgress, setOvertimeTicketProgress] = useState<OvertimeTicketProgress[]>(jobConfirms);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  // const uploadTicketFileMutation = useMutation({
  //   mutationFn: (body: FormData) => uploadFile(body as FormData)
  // });

  const beforeUpload = (file: RcFile) => {
    if (file.size <= 0) {
      showNotification(t('notification.warning.emptyFile'), 'warning');
    }

    const check = (formState?.overtimeTicketProgressAttachAdd as UploadFile<File>[])?.some(
      (item) => item.name === file.name && item.size === file.size && item.type === file.type
    );
    if (check) {
      showNotification(t('notification.warning.fileExist'), 'warning');
    }

    return (check || file.size <= 0) && Upload.LIST_IGNORE;
  };

  const getBase64 = (file: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as Blob);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  // const getFileList = async (fileLists: UploadFile<File>[]) => {
  //   const formData = new FormData();
  //   fileLists.length > 0 &&
  //     fileLists.forEach(function (file) {
  //       formData.append('files', file.originFileObj as Blob);
  //     });
  //   return uploadTicketFileMutation.mutateAsync(formData, {
  //     onError: () => {
  //       alert('Lỗi ảnh');
  //     }
  //   });
  // };

  const handleChangeAttach: UploadProps['onChange'] = ({ fileList }) => {
    const newFileList = fileList.map((item) => ({
      ...item,
      status: 'done' as UploadFileStatus
    }));
    setFormState((prev) => ({ ...prev, overtimeTicketProgressAttachAdd: newFileList }));
  };

  const handleSave = () => {
    form
      .validateFields()
      .then(() => {
        let cloneDataSource = [...overtimeTicketProgress];

        const index = cloneDataSource?.findIndex(
          (item) => item.manpowerSupplyFormDetailId === formState.manpowerSupplyFormDetailId
        );
        if (index === -1) return;

        cloneDataSource[index] = formState as OvertimeTicketProgress;

        setOvertimeTicketProgress(cloneDataSource);
        setFormState(initialFormState);
        setEditingKey('');

        handleSetFormState(cloneDataSource);
      })
      .catch(() => {});
  };

  const handleEdit = (record: OvertimeTicketProgress) => {
    setFormState(record);
    setAddMode('edit');
    setEditingKey(String(record.manpowerSupplyFormDetailId));
  };

  const handleCancel = (record: OvertimeTicketProgress) => {
    if (addMode === 'add') {
      setOvertimeTicketProgress(
        overtimeTicketProgress.filter((x) => x.manpowerSupplyFormDetailId !== record.manpowerSupplyFormDetailId)
      );
      setEditingKey('');
    } else {
      setFormState(initialFormState);
      setEditingKey('');
    }
  };

  return (
    <>
      <Form
        form={form}
        fields={[
          {
            name: 'percentageCompletion',
            value: formState.percentageCompletion
          },
          {
            name: 'isCompleted',
            value: formState.isCompleted
          },
          {
            name: 'note',
            value: formState?.note
          }
        ]}
      >
        <Table
          className='overtime-progress-confirm-table'
          columns={columns(
            formState,
            setFormState,
            handleSave,
            handleEdit,
            handleCancel,
            beforeUpload,
            handleChangeAttach,
            editingKey,
            isSupervisorConfirmed,
            t
          )}
          rowKey={(record) => String(record.manpowerSupplyFormDetailId)}
          dataSource={overtimeTicketProgress}
          pagination={false}
          scroll={{ x: 400 }}
          bordered={false}
          size='middle'
          expandable={{
            expandedRowKeys: overtimeTicketProgress?.map((item) => item.manpowerSupplyFormDetailId as number),
            expandedRowRender: (record) => (
              <Flex vertical gap={10}>
                <Space size={[4, 0]}>
                  {formState.manpowerSupplyFormDetailId === record.manpowerSupplyFormDetailId &&
                    formState.isCompleted && (
                      <Tooltip title='Bắt buộc'>
                        <Text type='danger'>*</Text>
                      </Tooltip>
                    )}

                  <Text strong>Hình ảnh kết quả thực hiện công việc</Text>
                </Space>

                <Form.Item
                  name='resultImage'
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      required:
                        formState.manpowerSupplyFormDetailId === record.manpowerSupplyFormDetailId &&
                        formState.isCompleted
                          ? true
                          : false,
                      message: 'Tải lên Hình ảnh kết quả thực hiện công việc'
                    }
                  ]}
                >
                  <Upload
                    multiple
                    beforeUpload={beforeUpload}
                    listType='picture-card'
                    showUploadList={{
                      showRemoveIcon: true,
                      showPreviewIcon: true
                    }}
                    onPreview={handlePreview}
                    accept='image/*'
                    fileList={
                      formState.manpowerSupplyFormDetailId === record.manpowerSupplyFormDetailId
                        ? (formState?.overtimeTicketProgressAttachAdd as UploadFile<File>[])
                        : (record?.overtimeTicketProgressAttachAdd as UploadFile<File>[])
                    }
                    onChange={handleChangeAttach}
                    style={{ width: '100%' }}
                  >
                    <Flex vertical className='upload-body'>
                      <button
                        style={{ border: 0, background: 'none', cursor: 'pointer' }}
                        type='button'
                        disabled={formState.manpowerSupplyFormDetailId !== record.manpowerSupplyFormDetailId}
                      >
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>Tải hình</div>
                      </button>
                    </Flex>
                  </Upload>
                </Form.Item>
              </Flex>
            )
            // expandIcon: () => <></>
          }}
        />
      </Form>
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)}>
        <img alt='example' style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </>
  );
}
